import type { QueryClient } from '@tanstack/react-query';
import type {
  BackgroundSearchOperation,
  BackgroundSearchQueue,
  BackgroundSearchTask,
} from '../types/background-search.types';

const MAX_CONCURRENT_SEARCHES = 4;
const queues = new WeakMap<QueryClient, BackgroundSearchQueue>();

/**
 * Limits optional board refreshes and index probes together, per query client.
 * Interactive reads bypass this FIFO queue. Operations must release their slot
 * before awaiting another queued operation, such as a probe's follow-up refresh.
 *
 * @param client - Cache owning the recovery work.
 * @param operation - One search and the conditions under which it is useful.
 * @returns Its result, or undefined when skipped before dispatch.
 */
export const scheduleBackgroundSearch = <T>(
  client: QueryClient,
  operation: BackgroundSearchOperation<T>
): Promise<T | undefined> => {
  const isRelevant = (): boolean =>
    !operation.signal?.aborted &&
    Date.now() < (operation.deadline ?? Infinity) &&
    operation.isRelevant();
  if (!isRelevant()) return Promise.resolve(undefined);

  let queue = queues.get(client);
  if (!queue) {
    queue = {
      active: 0,
      pending: [],
      stopped: false,
      unsubscribe: () => undefined,
    };
    queues.set(client, queue);
    const owner = queue;
    owner.unsubscribe = client.getQueryCache().subscribe((event) => {
      if (
        event.type === 'removed' &&
        client.getQueryCache().findAll({
          queryKey: ['crm/documents/funnel-conversations'],
        }).length === 0
      ) {
        owner.stopped = true;
        owner.pending.splice(0).forEach((task) => task.skip());
        owner.unsubscribe();
        if (queues.get(client) === owner) queues.delete(client);
      }
    });
  }
  const owner = queue;
  const drain = (): void => {
    while (!owner.stopped && owner.active < MAX_CONCURRENT_SEARCHES) {
      const task = owner.pending.shift();
      if (!task) break;
      if (task.isRelevant()) task.start();
      else task.skip();
    }
    if (owner.active === 0 && owner.pending.length === 0) {
      owner.unsubscribe();
      if (queues.get(client) === owner) queues.delete(client);
    }
  };

  return new Promise<T | undefined>((resolve, reject) => {
    let timer: number | undefined;
    const cleanup = (): void => {
      window.clearTimeout(timer);
      operation.signal?.removeEventListener('abort', cancelWaiting);
    };
    const release = (): void => {
      owner.active -= 1;
      drain();
    };
    const task: BackgroundSearchTask = {
      isRelevant,
      skip: () => {
        cleanup();
        resolve(undefined);
      },
      start: () => {
        cleanup();
        owner.active += 1;
        try {
          void operation.run().then(resolve, reject).finally(release);
        } catch (error) {
          reject(error instanceof Error ? error : new Error(String(error)));
          release();
        }
      },
    };
    const cancelWaiting = (): void => {
      const index = owner.pending.indexOf(task);
      if (index < 0) return;
      owner.pending.splice(index, 1);
      task.skip();
      drain();
    };
    operation.signal?.addEventListener('abort', cancelWaiting, { once: true });
    if (operation.deadline !== undefined) {
      timer = window.setTimeout(
        cancelWaiting,
        Math.max(0, operation.deadline - Date.now())
      );
    }
    owner.pending.push(task);
    drain();
  });
};
